import { cn } from "@/lib/utils";

// ============================================
// Badge - 점 형태 인디케이터
// ============================================

interface BadgeProps {
  /** 색상은 className으로 지정 (예: "bg-red-500 ring-white") */
  className?: string;
}

export const Badge = ({ className }: BadgeProps) => {
  return (
    <div className="flex items-center justify-center size-4">
      <div
        className={cn(
          "size-1.5 rounded-full ring-1",
          // 기본 색상: 빨간 점 + 흰색 stroke
          "bg-red-500 ring-white",
          className,
        )}
      />
    </div>
  );
};

// ============================================
// IconBadge - 아이콘이 포함된 원형 뱃지
// ============================================

interface IconBadgeProps {
  /** 색상은 className으로 지정 (예: "bg-green-500") */
  className?: string;
  children: React.ReactNode;
}

export const IconBadge = ({ className, children }: IconBadgeProps) => {
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center p-0.5 rounded-full",
        // 기본 색상: 초록 배경
        "bg-green-500",
        className,
      )}
    >
      <span className="flex items-center justify-center size-2">
        {children}
      </span>
    </div>
  );
};

// ============================================
// NumberBadge - 숫자/텍스트가 포함된 pill 뱃지
// ============================================

/** 건수 배지 상한 — 세 자리까지 그대로, 넘으면 999+ */
export const NUMBER_BADGE_COUNT_MAX = 999;

interface NumberBadgeProps {
  /** 색상은 className으로 지정 (예: "bg-green-500 text-black") */
  className?: string;
  children: React.ReactNode;
  /** 이 값을 넘으면 `${max}+` 로 표기 */
  max?: number;
}

export const NumberBadge = ({ className, children, max }: NumberBadgeProps) => {
  const number = Number(children);
  const displayNumber = max !== undefined && number > max ? `${max}+` : number;
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center min-w-[18px] min-h-[18px] px-1 py-0.5 rounded-full",
        "font-medium text-[11px] leading-[1.273] tracking-[0.34px]",
        // 기본 색상: 초록 배경 + 검정 텍스트
        "bg-green-500 text-black",
        className,
      )}
    >
      {displayNumber}
    </div>
  );
};

// ============================================
// AvatarBadge - 아바타 스타일 텍스트 뱃지
// ============================================

interface AvatarBadgeProps {
  /** 색상은 className으로 지정 */
  className?: string;
  children: React.ReactNode;
}

export const AvatarBadge = ({ className, children }: AvatarBadgeProps) => {
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center size-4 rounded-full",
        "border-[1.333px] border-white",
        "font-medium text-[8px] leading-[13.333px] tracking-[-0.24px]",
        // 기본 색상: 분홍색 배경 + 분홍색 텍스트 (Rouge)
        "bg-[#fdf1f7] text-[#973562]",
        className,
      )}
    >
      {children}
    </div>
  );
};
